import * as Sentry from "@sentry/react";
import { isBoolean } from "lodash";

import { GuidanceFileParameters, InferenceParameters } from "@/types";
import { getImageObjectSafe } from "@/utils/images";

import { DEFAULT_INFERENCE_FORM } from "../../../../features/forge/constants";
import { ReferenceTypeEnum } from "../../../ui-v2/References/types";
import {
  formatReferenceForAPI,
  getDefaultReference,
} from "../../../ui-v2/References/utils";
import { formatFormWithStyleValuesForAPI } from "../../fields/Style/utils";
import {
  formatAPIPromptLanguageForForm,
  getPercentageFromWeight,
  getSimilarityFromWeight,
} from "../../utils/utils";
import { FORM_FIELDS_2D } from "./constants";
import { FormType2D } from "./types";

export const formatInferenceParamsForForm = async (
  params: InferenceParameters,
): Promise<Partial<FormType2D>> => {
  const outputForm: Partial<FormType2D> = {};

  if (params.width && params.height) {
    outputForm[FORM_FIELDS_2D.DIMENSIONS] = [params.width, params.height];
  }
  if (params.styles?.length) {
    outputForm[FORM_FIELDS_2D.STYLE] = {
      id: params.styles[0].id,
      weight: getPercentageFromWeight(params.styles[0].weight),
    };
    outputForm[FORM_FIELDS_2D.STYLE_COMPONENTS] = params.styles.map((style) => {
      return {
        id: style.id,
        weight: getPercentageFromWeight(style.weight),
      };
    });
  }
  if (typeof params.prompt !== "undefined") {
    outputForm[FORM_FIELDS_2D.PROMPT_TEXT] = params.prompt;
  }
  if (typeof params.promptJson !== "undefined") {
    outputForm[FORM_FIELDS_2D.PROMPT_JSON] = params.promptJson;
  }
  if (typeof params.negativePrompt !== "undefined") {
    outputForm[FORM_FIELDS_2D.NEGATIVE_PROMPT_TEXT] = params.negativePrompt;
  }
  if (typeof params.negativePromptJson !== "undefined") {
    outputForm[FORM_FIELDS_2D.NEGATIVE_PROMPT_JSON] = params.negativePromptJson;
  }
  if (typeof params.promptLanguage !== "undefined") {
    outputForm[FORM_FIELDS_2D.PROMPT_LANGUAGE] = formatAPIPromptLanguageForForm(
      params.promptLanguage,
    );
  }

  if (isBoolean(params.transparency)) {
    outputForm[FORM_FIELDS_2D.TRANSPARENCY] = params.transparency;
  }

  if (isBoolean(params.tileability)) {
    outputForm[FORM_FIELDS_2D.TILEABILITY] = params.tileability;
  }

  if (params.quality) {
    outputForm[FORM_FIELDS_2D.PERFORMANCE] = params.quality;
  }

  if (typeof params.numInferenceSteps !== "undefined") {
    outputForm[FORM_FIELDS_2D.DENOISING_STEPS] = params.numInferenceSteps;
  }

  if (typeof params.guidanceScale !== "undefined") {
    outputForm[FORM_FIELDS_2D.GUIDANCE_SCALE] = params.guidanceScale;
  }

  if (typeof params.baseShift !== "undefined") {
    outputForm[FORM_FIELDS_2D.BASE_SHIFT] = params.baseShift;
  }

  if (typeof params.maxShift !== "undefined") {
    outputForm[FORM_FIELDS_2D.MAX_SHIFT] = params.maxShift;
  }

  if (typeof params.batchSize !== "undefined") {
    outputForm[FORM_FIELDS_2D.BATCH_SIZE] = params.batchSize;
  }

  if (params.files?.length) {
    outputForm[FORM_FIELDS_2D.REFERENCES] = await Promise.all(
      params.files.map(async (file) => {
        const type = file.type as ReferenceTypeEnum;
        const similarity = getSimilarityFromWeight(file.weight);
        let image;

        const imageObject = await getImageObjectSafe(file.url);
        if (imageObject) {
          image = {
            driveFileId: file.fileId,
            src: file.url,
            width: imageObject.width,
            height: imageObject.height,
            transform: file.transform,
          };
        }
        return getDefaultReference({ type, image, similarity });
      }),
    );
  }

  return outputForm;
};

// Formats form values for inference and session apis
export const formatFormValuesForCommonAPI = (
  inputForm: FormType2D,
): InferenceParameters => {
  let formattedReferences: GuidanceFileParameters[] = [];

  try {
    formattedReferences = inputForm[FORM_FIELDS_2D.REFERENCES].reduce<
      GuidanceFileParameters[]
    >((acc, ref) => {
      if (!!ref.image) {
        acc.push(
          formatReferenceForAPI(
            ref as typeof ref & { image: NonNullable<typeof ref.image> },
          ),
        );
      }
      return acc;
    }, []);
  } catch (e) {
    // Adding this here to debug this error:
    // https://layerai.sentry.io/issues/5947709641/
    Sentry.captureException(e, {
      extra: { references: inputForm[FORM_FIELDS_2D.REFERENCES] },
    });
  }

  const [width, height] = inputForm[FORM_FIELDS_2D.DIMENSIONS];

  const parameters = {
    ...DEFAULT_INFERENCE_FORM.parameters,
    ...formatFormWithStyleValuesForAPI(inputForm),
    width,
    height,
    negativePrompt: inputForm[FORM_FIELDS_2D.NEGATIVE_PROMPT_TEXT],
    prompt: inputForm[FORM_FIELDS_2D.PROMPT_TEXT],
    promptLanguage: inputForm[FORM_FIELDS_2D.PROMPT_LANGUAGE],
    transparency: inputForm[FORM_FIELDS_2D.TRANSPARENCY],
    tileability: inputForm[FORM_FIELDS_2D.TILEABILITY],
    seed: inputForm[FORM_FIELDS_2D.SEED] || -1,
    files: formattedReferences,
    numInferenceSteps: inputForm[FORM_FIELDS_2D.DENOISING_STEPS],
    guidanceScale: inputForm[FORM_FIELDS_2D.GUIDANCE_SCALE],
    quality: inputForm[FORM_FIELDS_2D.PERFORMANCE],
    baseShift: inputForm[FORM_FIELDS_2D.BASE_SHIFT],
    maxShift: inputForm[FORM_FIELDS_2D.MAX_SHIFT],
    batchSize: inputForm[FORM_FIELDS_2D.BATCH_SIZE],
  };

  return parameters;
};

export const formatFormValuesForInferenceAPI = (
  inputForm: FormType2D,
): InferenceParameters => ({
  ...formatFormValuesForCommonAPI(inputForm),
});
